// Updated App.tsx with stable layout fix
import { Refine } from "@refinedev/core";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";
import {
  ErrorComponent,
  ThemedLayoutV2,
} from "@refinedev/antd";
import { customNotificationProvider } from "./providers/customNotificationProvider";

import "@refinedev/antd/dist/reset.css";

import routerBindings, {
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from "@refinedev/react-router-v6";
import { App as AntdApp, ConfigProvider } from "antd";
import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";
import { authProvider } from "./providers/authProvider";
import { dataProvider } from "./providers/dataProvider";
import {
  Dashboard,
  Dashboard2,
  Videos,
  Shares,
  Profile,
  Billing,
  Usage,
  Login,
  Register,
  ForgotPassword,
  Admin,
  CompanySetup,
  HelpSupport
} from "./pages";
import { PublicShare } from "./pages/PublicShare";
import { CustomSider } from "./components/layouts/CustomSider";
import { CustomHeader } from "./components/layouts/CustomHeader";
import { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./utils/firebase";

// Custom Layout component with mobile support - STABLE VERSION
const ResponsiveLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <>
      <CustomSider />
      <div 
        style={{ 
          paddingLeft: isMobile ? 0 : 256, // Use padding instead of margin
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          transition: 'padding-left 0.3s',
        }}
      >
        <CustomHeader />
        <main
          style={{
            flex: 1,
            padding: isMobile ? '16px' : '24px',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}
        >
          {children}
        </main>
      </div>
    </>
  );
};

function App() {
  // auth monitor
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const publicPaths = ['/login', '/register', '/forgot-password'];
      const currentPath = window.location.pathname;

      // Allow public share pages without auth
      if (currentPath.startsWith('/public/share/') || currentPath.startsWith('/share/')) {
        return;
      }

      if (!user && !publicPaths.includes(currentPath)) {
        // No authenticated user and not on a public page
        window.location.href = '/login';
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <BrowserRouter>
      <RefineKbarProvider>
        <ConfigProvider
          theme={{
            token: {
              // Add responsive breakpoints
              screenXS: 480,
              screenSM: 576,
              screenMD: 768,
              screenLG: 992,
              screenXL: 1200,
              screenXXL: 1600,
            },
          }}
        >
          <AntdApp>
            <Refine
              dataProvider={dataProvider}
              authProvider={authProvider}
              routerProvider={routerBindings}
              notificationProvider={customNotificationProvider}
              resources={[
                {
                  name: "dashboard",
                  list: "/dashboard",
                  meta: {
                    label: "New Inspection Review",
                    icon: "📊",
                    title: "Spectreviews - New Review",
                  },
                },
                {
                  name: "videos",
                  list: "/videos",
                  meta: {
                    label: "My Reviews",
                    icon: "🎥",
                    title: "Spectreviews - My Reviews",
                  },
                },
                {
                  name: "admin",
                  list: "/admin",
                  meta: {
                    label: "Admin",
                    icon: "⚙️",
                    hide: true,
                    title: "Spectreviews - Admin",
                  },
                },
                {
                  name: "help-support",
                  list: "/help-support",
                  meta: {
                    label: "Help & Support",
                    icon: "❓",
                    title: "Help & Support - Spectreviews AI",
                  },
                },
                {
                  name: "company-setup",
                  list: "/company-setup",
                  meta: {
                    label: "Company Setup",
                    icon: "⚙️",
                    hide: true,
                    title: "Spectreviews - Setup",
                  },
                },
              ]}
              options={{
                syncWithLocation: true,
                warnWhenUnsavedChanges: true,
                useNewQueryKeys: true,
                projectId: "spectreviews",
              }}
            >
              <Routes>
                <Route element={<ResponsiveLayout><Outlet /></ResponsiveLayout>}>
                <Route index element={<NavigateToResource resource="dashboard" />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/dashboard2" element={<Dashboard2 />} />
                  <Route path="/videos" element={<Videos />} />
                  <Route path="/shares" element={<Videos />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/help-support" element={<HelpSupport />} />
                  <Route path="/company-setup" element={<CompanySetup />} />

                  {/* Profile dropdown routes - not in sidebar */}
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/billing" element={<Billing />} />
                  <Route path="/usage" element={<Usage />} />
                </Route>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                {/* Public share routes - no auth required */}
                <Route path="/public/share/:token" element={<PublicShare />} />
                <Route path="/share/:token" element={<PublicShare />} />
                <Route path="*" element={<ErrorComponent />} />
              </Routes>

              <RefineKbar />
              <UnsavedChangesNotifier />
              <DocumentTitleHandler 
                handler={(props) => {
                  const pathname = props?.pathname || '/';
                  const titles: Record<string, string> = {
                    '/dashboard': 'New Review - Spectreviews AI',
                    '/videos': 'My Reviews - Spectreviews AI',
                    '/profile': 'Profile - Spectreviews AI',
                    '/company-setup': 'Setup - Spectreviews AI',
                    '/billing': 'Billing - Spectreviews AI',
                    '/admin': 'Admin - Spectreviews AI',
                    '/login': 'Login - Spectreviews AI',
                    '/register': 'Register - Spectreviews AI',
                  };
                  return titles[pathname] || 'Spectreviews - AI Generated Home Inspection Video Reviews';
                }} 
              />
            </Refine>
          </AntdApp>
        </ConfigProvider>
      </RefineKbarProvider>
    </BrowserRouter>
  );
}

export default App;

// Add this CSS to your global styles or App.css
const globalStyles = `
/* Mobile responsive utilities */
@media (max-width: 767px) {
  /* Hide ant-layout-sider on mobile since we use drawer */
  .ant-layout-sider {
    display: none !important;
  }
  
  /* Adjust card and form layouts for mobile */
  .ant-card {
    margin: 8px 0;
  }
  
  .ant-form-item-label {
    padding: 0 !important;
  }
  
  /* Make tables horizontally scrollable on mobile */
  .ant-table-wrapper {
    overflow-x: auto;
  }
  
  /* Adjust modal sizes for mobile */
  .ant-modal {
    max-width: calc(100vw - 32px) !important;
    margin: 16px auto;
  }
  
  /* Stack form items vertically on mobile */
  .ant-row.ant-form-item {
    flex-direction: column;
  }
  
  /* Full width buttons on mobile */
  .ant-btn {
    width: 100%;
    margin-bottom: 8px;
  }
  
  .ant-btn-group {
    display: flex;
    flex-direction: column;
    width: 100%;
  }
  
  .ant-btn-group .ant-btn {
    border-radius: 4px !important;
    margin-bottom: 8px;
  }
}

/* Ensure content doesn't get hidden behind fixed elements */
.ant-layout-content {
  min-height: 100vh;
}

/* Make file upload area more touch-friendly on mobile */
@media (max-width: 767px) {
  .ant-upload-drag {
    padding: 40px 16px !important;
  }
  
  .ant-upload-drag-icon {
    font-size: 48px !important;
  }
}

/* Add this minimal CSS to your index.css or App.css */

/* Force stable table layout to prevent reflow */
.ant-table-wrapper {
  min-height: 400px;
}

.ant-table-placeholder {
  min-height: 400px;
}

/* Ensure cards maintain consistent height */
.ant-card {
  min-height: 200px;
}

/* Prevent header from causing reflow */
.ant-layout-header {
  flex-shrink: 0;
}

/* Ensure consistent sidebar width */
.ant-layout-sider {
  flex-shrink: 0;
}

/* Prevent content area from jumping */
.ant-layout-content {
  overflow-y: auto;
  overflow-x: hidden;
}
`;

// Add this style tag to your index.html or create a separate CSS file
// <style>${globalStyles}</style>